import React from 'react';
import { Box, Unstable_Grid2 as Grid } from '@mui/material';

import { Container, Select, StackedBarChart, Table } from 'components';
import { useTranslations } from 'hooks/useTranslations';

import { CustomColor, customColors } from 'config/customColors';
import { CustomString } from 'config/translations';
import { UseQuery } from 'useQuery';
import { CashflowCategory } from 'useQuery/types';

type CategoryId =
  | 'mrr'
  | 'usageFee'
  | 'oneTimeFee'
  | 'mywheelsCommission'
  | 'greenwheelsCommission'
  | 'greenwheels_rnpCommission';

type VatId = 'excl' | 'incl';

// What Figo invoices for itself. The mobility spend that is passed on to the
// providers is not part of the cashflow, so this tab has no filters: the
// endpoint returns these categories and nothing else.
const CATEGORIES: { id: CategoryId; tag: CashflowCategory; color: CustomColor }[] = [
  { id: 'mrr', tag: 'SUBSCRIPTION', color: customColors.mrr },
  { id: 'usageFee', tag: 'USAGE_FEE', color: customColors.usage_fee },
  { id: 'oneTimeFee', tag: 'ONETIMEFEE', color: customColors.one_time_fee },
  {
    id: 'mywheelsCommission',
    tag: 'MYWHEELS Commission',
    color: customColors.mywheels_commission,
  },
  {
    id: 'greenwheelsCommission',
    tag: 'GREENWHEELS Commission',
    color: customColors.greenwheels_commission,
  },
  {
    id: 'greenwheels_rnpCommission',
    tag: 'GREENWHEELS_RNP Commission',
    color: customColors.greenwheels_rnp_commission,
  },
];

// Cashflow counts an invoice in the month it was sent: a yearly subscription
// lands in that one month instead of being spread out over the year, which is
// what the Business tab does.
export function CashflowStatistics() {
  const { data: cashflowStatisticsData } = UseQuery.cashflowStatistics();
  const translations = useTranslations('statisticsPage');

  const [vat, setVat] = React.useState<VatId>('excl');

  const vatOptions: { id: VatId; label: CustomString }[] = [
    { id: 'excl', label: translations.vatExcl },
    { id: 'incl', label: translations.vatIncl },
  ];

  // Both variants are in the same response, so switching does not refetch.
  const spend = vat === 'incl' ? cashflowStatisticsData?.spend : cashflowStatisticsData?.spend_net;
  const byMonth = spend?.by_month ?? [];

  return (
    <Grid container spacing={2} sx={{ paddingTop: 1 }}>
      <Container
        title={translations.cashflowInvoiced}
        width="flex"
        loading={cashflowStatisticsData === undefined}
        headerAction={
          <Box sx={{ width: 160 }}>
            <Select
              mode="single"
              label={translations.vat}
              value={vat}
              options={vatOptions}
              onChange={setVat}
              fullWidth
            />
          </Box>
        }
      >
        <>
          <StackedBarChart
            key={vat}
            labels={byMonth.map((item) => Object.keys(item)[0])}
            data={CATEGORIES.map(({ id, tag }) => ({
              label: translations[id],
              values: byMonth.map((item) => {
                const key = Object.keys(item)[0];

                return item[key] ? (item[key][tag] ?? 0) / 100 : 0;
              }),
            }))}
            colors={CATEGORIES.map(({ color }) => color)}
          />
          <Box sx={{ width: '100%' }}>
            <Table
              columns={{
                total: { name: translations.total, type: 'price', flex: 1 },
              }}
              rows={[{ id: 'total', total: spend?.total ?? 0 }]}
              loading={!spend}
              useMobileView={false}
            />
          </Box>
        </>
      </Container>
    </Grid>
  );
}
